import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "../lib/api";
import { CheckCircle2, ArrowLeft, Loader2, Calendar } from "lucide-react";
import { track } from "../lib/analytics";

export const BusinessSuccess = () => {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sid) { setStatus("missing"); return; }
    http.get(`/business/checkout/${sid}`)
      .then((r) => {
        setData(r.data);
        setStatus("ok");
        if (r.data.payment_status === "paid") {
          track("business_purchase", {
            transaction_id: sid,
            value: (r.data.amount_total || 0) / 100,
            currency: "USD",
            tier_id: r.data.inquiry?.tier_id,
            tier_name: r.data.inquiry?.tier_name,
          });
        }
      })
      .catch(() => setStatus("error"));
  }, [sid]);

  const inq = data?.inquiry;
  const paid = data?.payment_status === "paid";

  return (
    <div data-testid="business-success-page" className="mx-auto max-w-2xl px-6 py-24 text-center">
      {status === "loading" && (
        <div className="text-neutral-400 flex items-center justify-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin" /> confirming your booking...
        </div>
      )}
      {status === "ok" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-[color:var(--accent-matrix)] mx-auto mb-6" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// booking confirmed</div>
          <h1 className="font-display font-bold text-4xl tracking-tight mb-4">welcome to the roster.</h1>

          {inq && (
            <div className="mt-6 mb-6 border border-white/10 bg-[#0a0a0a] p-6 text-left">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">// engagement</div>
              <div className="font-display font-bold text-2xl">{inq.tier_name}</div>
              <div className="text-sm text-neutral-400 mt-2">
                {paid ? "payment received" : "payment processing"}
                {typeof data?.amount_total === "number" && ` · $${(data.amount_total / 100).toFixed(2)}${inq.cadence === "monthly" ? "/mo" : ""}`}
              </div>
              <div className="text-xs text-neutral-500 mt-3">
                confirmation sent to <span className="text-neutral-300">{inq.email}</span>
              </div>
            </div>
          )}

          <p className="text-neutral-400 mb-8 leading-relaxed">
            {paid
              ? "you'll get a kickoff email within 24 hours with next steps, a shared workspace link, and a scheduling link for the discovery call."
              : "your payment is being confirmed. you'll hear from us shortly."}
          </p>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/business"
              data-testid="business-success-back"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest border border-white/15 hover:border-[color:var(--accent-amber)] px-4 py-2 text-neutral-300 duration-150"
              style={{ transitionProperty: "border-color" }}
            >
              <ArrowLeft className="w-3 h-3" /> back to tiers
            </Link>
            <a
              href="mailto:hello@saimonsays.ai"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest border border-[color:var(--accent-matrix)] hover:bg-[color:var(--accent-matrix)] hover:text-black px-4 py-2 text-[color:var(--accent-matrix)] duration-150"
              style={{ transitionProperty: "background-color, color" }}
            >
              <Calendar className="w-3 h-3" /> reply to schedule
            </a>
          </div>
        </>
      )}
      {status === "error" && (
        <div className="text-neutral-400">couldn't verify booking. reply to your Stripe receipt if this persists.</div>
      )}
      {status === "missing" && (
        <div className="text-neutral-400">no session id found. <Link to="/business" className="text-[color:var(--accent-amber)]">back to tiers</Link></div>
      )}
    </div>
  );
};

export default BusinessSuccess;
